/* PDP Video Guards (mobile + desktop) — v2.1 (2025-08-30)
   - Mobile: on returning to the video slide, restart from 0 and keep it playing briefly (DOM-replacement safe).
   - Desktop: initial autostart + return-to-video on EVERY switch with short guard (IO-based).
   - Scope: product page section only. Safe to include defer-loaded.
*/
(function(){
  'use strict';

  // --- Resolve the product section root reliably, even if loaded inside the section
  function getPDP(){
    const s = document.currentScript;
    return (s && s.closest && s.closest('[data-section-type="product"]'))
        || document.querySelector('[data-section-type="product"][data-is-full-product="true"]')
        || document.querySelector('[data-section-type="product"]');
  }

  function visible(node){
    if (!node) return false;
    const cs = getComputedStyle(node);
    return cs.display !== 'none' && cs.visibility !== 'hidden' && node.offsetWidth > 0 && node.offsetHeight > 0;
  }

  // --- Common helpers
  const SLIDE_SEL  = '.product__media-item[data-media-item-id]';
  const VIDEO_ATTR = '[data-media-type="video"]';
  const VIDEO_Q    = 'video';

  function prep(v){
    if (!v) return;
    v.preload = 'auto';
    v.muted = true;
    v.playsInline = true; v.setAttribute('playsinline',''); v.setAttribute('webkit-playsinline','');
    v.autoplay = true;
    v.controls = false; v.removeAttribute('controls');
    v.setAttribute('controlsList','nodownload noplaybackrate nofullscreen');
    v.setAttribute('disablepictureinpicture','');
    if (!v.__wcHideMO){
      const mo = new MutationObserver(()=>{ if (v.controls){ v.controls=false; v.removeAttribute('controls'); } });
      mo.observe(v, { attributes:true, attributeFilter:['controls'] });
      v.__wcHideMO = mo;
    }
  }
  function playFromStart(v){
    if (!v) return;
    prep(v);
    try { v.currentTime = 0; } catch(_) {}
    let tries = 0;
    (function attempt(){
      const p = v.play();
      if (p && typeof p.then === 'function'){
        p.catch(()=>{ if (++tries <= 14){ try{ v.load(); }catch(_){} setTimeout(attempt, 90); } });
      }
    })();
  }
  function resume(v){
    if (!v || v.ended) return;
    prep(v);
    let tries = 0;
    (function attempt(){
      if (!v.paused) return;
      const p = v.play();
      if (p && typeof p.then === 'function'){
        p.catch(()=>{ if (++tries <= 12){ setTimeout(attempt, 100); } });
      }
    })();
  }
  function areaWithin(el, root){
    const r  = el.getBoundingClientRect();
    const rr = (root && root.getBoundingClientRect ? root.getBoundingClientRect() : r);
    const x = Math.max(r.left, rr.left), y = Math.max(r.top, rr.top);
    const w = Math.max(0, Math.min(r.right, rr.right) - x);
    const h = Math.max(0, Math.min(r.bottom, rr.bottom) - y);
    return w * h;
  }
  function areaOnPage(el){
    const r = el.getBoundingClientRect();
    const x = Math.max(r.left, 0), y = Math.max(r.top, 0);
    const w = Math.max(0, Math.min(r.right, innerWidth)  - x);
    const h = Math.max(0, Math.min(r.bottom, innerHeight) - y);
    return w * h;
  }

  // --- MOBILE guard (only mobile/touch)
  function bootMobile(PDP){
    const isMobile = matchMedia('(hover:none), (pointer:coarse)').matches;
    if (!isMobile) return;

    const VP =
      PDP.querySelector('.product__media-container.below-mobile .scroll-slider__slider') ||
      PDP.querySelector('.product__media-container.below-mobile .product__media');
    if (!VP || VP.__wcMobileGuardBound) return;
    VP.__wcMobileGuardBound = true;

    function getDominantVideo(){
      const slides = Array.from(PDP.querySelectorAll(SLIDE_SEL));
      let best = null, bestA = 0;
      for (const s of slides){
        const a = areaWithin(s, VP);
        if (a > bestA){ bestA = a; best = s; }
      }
      if (!best || !best.matches(VIDEO_ATTR)) return null;
      if (bestA <= 0 || areaOnPage(best) <= 0) return null;
      return best.querySelector(VIDEO_Q);
    }

    let guardVideo = null, guardUntil = 0, guardTimer = null, lastTime = 0, lastTick = 0;
    function clearGuard(){
      if (guardTimer){ clearTimeout(guardTimer); guardTimer = null; }
      if (guardVideo){
        guardVideo.removeEventListener('pause', onPause, true);
        guardVideo.removeEventListener('waiting', onPause, true);
        guardVideo.removeEventListener('stalled', onPause, true);
        guardVideo.removeEventListener('suspend', onPause, true);
        guardVideo.removeEventListener('timeupdate', onTime, true);
        guardVideo = null;
      }
    }
    function onTime(){
      if (!guardVideo) return;
      const t = guardVideo.currentTime||0;
      if (t !== lastTime){ lastTime=t; lastTick=Date.now(); }
    }
    function onPause(){
      if (!guardVideo || Date.now() > guardUntil) return;
      const v = getDominantVideo();
      if (v && (v === guardVideo || !guardVideo.isConnected)){ resume(v); }
    }
    function tick(){
      if (!guardVideo) return;
      if (Date.now() > guardUntil) { clearGuard(); return; }
      if (!guardVideo.isConnected){
        const v2 = getDominantVideo();
        if (v2){ playFromStart(v2); startGuard(v2); return; }
        clearGuard(); return;
      }
      if ((Date.now() - lastTick) > 500 && !guardVideo.ended){ resume(guardVideo); }
      guardTimer = setTimeout(tick, 160);
    }
    function startGuard(v){
      clearGuard();
      guardVideo = v; guardUntil = Date.now() + 2000; // ~2s
      lastTime = v.currentTime||0; lastTick = Date.now();
      v.addEventListener('pause', onPause, true);
      v.addEventListener('waiting', onPause, true);
      v.addEventListener('stalled', onPause, true);
      v.addEventListener('suspend', onPause, true);
      v.addEventListener('timeupdate', onTime, true);
      tick();
    }

    const io = new IntersectionObserver(() => {
      const v = getDominantVideo();
      if (!v) return;
      if (v.paused) playFromStart(v);
      startGuard(v);
    }, { root: VP, threshold: [0,0.25,0.5,0.55,0.75] });

    function observe(){
      io.disconnect();
      PDP.querySelectorAll(SLIDE_SEL).forEach(s => io.observe(s));
    }
    observe();

    const mo = new MutationObserver(() => { observe(); });
    mo.observe(VP, { childList:true, subtree:true });

    ['scroll','touchend','pointerup','mouseup','transitionend','animationend'].forEach(ev=>{
      VP.addEventListener(ev, () => requestAnimationFrame(()=>io.takeRecords()), { passive:true });
    });

    document.addEventListener('visibilitychange', ()=>{ if (document.hidden) clearGuard(); });
    window.addEventListener('pageshow', ()=>{ const v=getDominantVideo(); if (v && v.paused){ playFromStart(v); startGuard(v);} });
  }

  // --- DESKTOP: initial kick + return guard on every switch (above-mobile viewport)
  function bootDesktop(PDP){
    const isDesktop = matchMedia('(hover:hover) and (pointer:fine)').matches;
    if (!isDesktop) return;
    if (PDP.__wcDesktopBound) return;                 // prevent duplicates
    PDP.__wcDesktopBound = PDP.__wcDesktopKick = true;

    const VP =
      PDP.querySelector('.product__media-container.above-mobile .scroll-slider__slider') ||
      PDP.querySelector('.product__media-container.above-mobile .product__media');
    if (!VP) return;

    function ratioWithin(el, root){
      const r  = el.getBoundingClientRect();
      const rr = (root.getBoundingClientRect ? root.getBoundingClientRect() : r);
      const x = Math.max(r.left, rr.left), y = Math.max(r.top, rr.top);
      const w = Math.max(0, Math.min(r.right, rr.right) - x);
      const h = Math.max(0, Math.min(r.bottom, rr.bottom) - y);
      const vis = w*h, full = Math.max(1, r.width*r.height);
      return vis / full;
    }
    function getDominantVideoDesktop(){
      const slides = Array.from(PDP.querySelectorAll(SLIDE_SEL));
      let best = null, bestR = 0;
      for (const s of slides){
        const rr = ratioWithin(s, VP);
        if (rr > bestR){ bestR = rr; best = s; }
      }
      if (!best || !best.matches(VIDEO_ATTR)) return null;
      if (bestR < 0.58) return null;                 // dominant enough in viewport
      if (areaOnPage(best) <= 0) return null;        // visible on page
      return best.querySelector(VIDEO_Q) || null;
    }

    function startGuard(v){
      let guardVideo = v;
      let guardUntil = Date.now() + 3000;            // ~3s
      let tm = null, last = v.currentTime||0, lastAt = Date.now();

      function cleanup(){
        if (tm){ clearTimeout(tm); tm = null; }
        if (!guardVideo) return;
        guardVideo.removeEventListener('timeupdate', onTime,   true);
        guardVideo.removeEventListener('pause',      onPause,  true);
        guardVideo.removeEventListener('waiting',    onPause,  true);
        guardVideo.removeEventListener('stalled',    onPause,  true);
        guardVideo.removeEventListener('suspend',    onPause,  true);
        guardVideo = null;
      }
      function onTime(){
        if (!guardVideo) return;
        const t = guardVideo.currentTime||0;
        if (t !== last){ last = t; lastAt = Date.now(); }
      }
      function onPause(){
        if (!guardVideo) return;
        if (Date.now() > guardUntil) return cleanup();
        // if DOM replaced or theme paused → reacquire current dominant video
        if (!guardVideo.isConnected){
          const v2 = getDominantVideoDesktop();
          if (v2){ playFromStart(v2); cleanup(); startGuard(v2); }
          else cleanup();
          return;
        }
        prep(guardVideo);
        guardVideo.play().catch(()=>{});
      }
      function tick(){
        if (!guardVideo) return;
        if (Date.now() > guardUntil) return cleanup();
        if (!guardVideo.isConnected){
          const v2 = getDominantVideoDesktop();
          if (v2){ playFromStart(v2); cleanup(); startGuard(v2); return; }
          return cleanup();
        }
        if ((Date.now() - lastAt) > 600 && !guardVideo.ended){
          prep(guardVideo);
          guardVideo.play().catch(()=>{});
        }
        tm = setTimeout(tick, 180);
      }

      guardVideo.addEventListener('timeupdate', onTime,  true);
      guardVideo.addEventListener('pause',      onPause, true);
      guardVideo.addEventListener('waiting',    onPause, true);
      guardVideo.addEventListener('stalled',    onPause, true);
      guardVideo.addEventListener('suspend',    onPause, true);
      tick();
    }

    // 1) Initial page load kick (in case IO doesn't fire during build/animation)
    (function bootstrap(){
      let tries = 0;
      function run(){
        const v = getDominantVideoDesktop();
        if (v){
          if (v.paused) playFromStart(v);
          startGuard(v);
          return;
        }
        if (++tries <= 8) setTimeout(run, 120);
      }
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
      else run();
      setTimeout(run, 0);
      setTimeout(run, 120);
      setTimeout(run, 320);
      document.addEventListener('shopify:section:load', run);
      window.addEventListener('pageshow', run);
    })();

    // 2) Return-to-video on EVERY switch: observe above-mobile viewport
    const io = new IntersectionObserver(() => {
      const v = getDominantVideoDesktop();
      if (!v) return;
      if (v.paused) playFromStart(v);
      startGuard(v);
    }, { root: VP, threshold: [0, 0.25, 0.5, 0.58, 0.75, 0.9] });

    function observeSlides(){
      io.disconnect();
      PDP.querySelectorAll(SLIDE_SEL).forEach(s => io.observe(s));
    }
    observeSlides();

    // Reobserve on DOM churn inside the viewport
    const mo = new MutationObserver(observeSlides);
    mo.observe(VP, { childList:true, subtree:true });

    // Nudge IO after user interactions / slider animations
    ['scroll','pointerup','mouseup','transitionend','animationend'].forEach(ev=>{
      VP.addEventListener(ev, () => requestAnimationFrame(()=>io.takeRecords()), { passive:true });
    });
  }

  // --- Boot when the section is present (and re-boot if it arrives late)
  function tryBoot(){
    const PDP = getPDP();
    if (!PDP) return false;
    bootMobile(PDP);
    bootDesktop(PDP);
    return true;
  }

  if (!tryBoot()){
    // If section not yet in DOM (rare), observe until it appears
    const mo = new MutationObserver(() => { if (tryBoot()){ mo.disconnect(); } });
    mo.observe(document.documentElement, { childList:true, subtree:true });
  }
})();